import { useCallback, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Copy,
  ArrowLeft,
  ArrowRight,
  Loader2,
} from 'lucide-react'
import { useFuelCsvImport, type ImportStep } from '@/hooks/useFuelCsvImport'
import type { FuelCsvField } from '@/lib/csv/fuel-csv-parser'
import { formatFuelTotal } from '@/types/fuel-transactions'

// ============================================
// Props
// ============================================

interface FuelCsvImportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// ============================================
// Constants
// ============================================

const EXTERNAL_SYSTEMS = [
  'Titan Aviation Fuels',
  'Avfuel',
  'World Fuel Services',
  'X1FBO',
  'Other',
]

const FIELD_OPTIONS: { value: FuelCsvField; label: string }[] = [
  { value: 'invoice_date', label: 'Invoice Date' },
  { value: 'invoice_number', label: 'Invoice Number' },
  { value: 'customer_name', label: 'Customer Name' },
  { value: 'tail_number', label: 'Tail Number' },
  { value: 'product', label: 'Fuel Type' },
  { value: 'gallons', label: 'Gallons' },
  { value: 'unit_price', label: 'Unit Price' },
  { value: 'total_amount', label: 'Total Amount' },
]

// ============================================
// Component
// ============================================

export function FuelCsvImportDialog({ open, onOpenChange }: FuelCsvImportDialogProps) {
  const {
    state,
    parseFile,
    updateMapping,
    resolveRows,
    executeImport,
    reset,
  } = useFuelCsvImport()

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        // Don't allow closing during import
        if (state.step === 'importing') return
        reset()
      }
      onOpenChange(nextOpen)
    },
    [state.step, reset, onOpenChange]
  )

  const stepTitle: Record<ImportStep, string> = {
    upload: 'Import Fuel Transactions from CSV',
    mapping: 'Map Columns',
    preview: 'Preview Import',
    importing: 'Importing...',
    complete: 'Import Complete',
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{stepTitle[state.step]}</DialogTitle>
          <DialogDescription>
            {state.step === 'upload' && 'Upload a CSV file from your fuel supplier to bulk-import transactions.'}
            {state.step === 'mapping' && 'Verify the column mapping. Adjust any incorrect mappings below.'}
            {state.step === 'preview' && 'Review resolved rows before importing. Only Ready and Warning rows will be imported.'}
            {state.step === 'importing' && 'Please wait while transactions are being created...'}
            {state.step === 'complete' && 'Import has finished.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {state.step === 'upload' && (
            <UploadStep
              externalSystem={state.externalSystem}
              onParse={parseFile}
            />
          )}

          {state.step === 'mapping' && (
            <MappingStep
              headers={state.headers}
              mapping={state.mapping}
              rawRows={state.rawRows}
              onUpdateMapping={updateMapping}
            />
          )}

          {state.step === 'preview' && (
            <PreviewStep
              rows={state.parsedRows}
              summary={state.summary}
            />
          )}

          {state.step === 'importing' && (
            <ImportingStep progress={state.importProgress} />
          )}

          {state.step === 'complete' && (
            <CompleteStep result={state.importResult} />
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {state.step === 'mapping' && (
            <>
              <Button variant="outline" onClick={reset}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button onClick={resolveRows}>
                Preview
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}

          {state.step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => reset()}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Start Over
              </Button>
              <Button
                onClick={executeImport}
                disabled={!state.summary || (state.summary.ready + state.summary.warnings) === 0}
              >
                Import {state.summary ? state.summary.ready + state.summary.warnings : 0} Rows
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}

          {state.step === 'complete' && (
            <Button onClick={() => handleOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Upload Step
// ============================================

function UploadStep({
  externalSystem,
  onParse,
}: {
  externalSystem: string
  onParse: (file: File, system: string) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const systemRef = useRef(externalSystem)

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      const file = e.dataTransfer.files[0]
      if (file && file.name.endsWith('.csv')) {
        onParse(file, systemRef.current)
      }
    },
    [onParse]
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        onParse(file, systemRef.current)
      }
    },
    [onParse]
  )

  return (
    <div className="space-y-4 py-4">
      {/* External system selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Fuel Supplier / Source</label>
        <Select
          defaultValue={externalSystem}
          onValueChange={(v) => { systemRef.current = v }}
        >
          <SelectTrigger className="w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EXTERNAL_SYSTEMS.map((sys) => (
              <SelectItem key={sys} value={sys}>
                {sys}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Drop zone */}
      <div
        className="flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/50 p-12 transition-colors hover:border-muted-foreground/50 cursor-pointer"
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileRef.current?.click()}
      >
        <Upload className="h-10 w-10 text-muted-foreground" />
        <div className="text-center">
          <p className="text-sm font-medium">
            Drop a .csv file here or click to browse
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Supports Titan, Avfuel, World Fuel, and custom CSV formats
          </p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    </div>
  )
}

// ============================================
// Mapping Step
// ============================================

function MappingStep({
  headers,
  mapping,
  rawRows,
  onUpdateMapping,
}: {
  headers: string[]
  mapping: Map<number, FuelCsvField>
  rawRows: Record<string, string>[]
  onUpdateMapping: (index: number, field: FuelCsvField | null) => void
}) {
  // Reverse mapping to know which fields are already used
  const usedFields = new Set(mapping.values())

  // Get sample values from first row
  const sampleRow = rawRows[0] ?? {}

  return (
    <ScrollArea className="h-[400px] pr-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-2 pr-4 font-medium">CSV Column</th>
            <th className="text-left py-2 pr-4 font-medium">Mapped Field</th>
            <th className="text-left py-2 font-medium">Sample Value</th>
          </tr>
        </thead>
        <tbody>
          {headers.map((header, idx) => {
            const currentField = mapping.get(idx)
            const sampleValue = sampleRow[String(idx)] ?? ''

            return (
              <tr key={idx} className="border-b">
                <td className="py-2 pr-4">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="truncate">{header}</span>
                  </div>
                </td>
                <td className="py-2 pr-4">
                  <Select
                    value={currentField ?? '__skip__'}
                    onValueChange={(v) =>
                      onUpdateMapping(idx, v === '__skip__' ? null : (v as FuelCsvField))
                    }
                  >
                    <SelectTrigger className="w-44 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__skip__">
                        <span className="text-muted-foreground">— Skip —</span>
                      </SelectItem>
                      {FIELD_OPTIONS.map((opt) => (
                        <SelectItem
                          key={opt.value}
                          value={opt.value}
                          disabled={usedFields.has(opt.value) && currentField !== opt.value}
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="py-2">
                  <span className="text-muted-foreground font-mono text-xs truncate block max-w-48">
                    {sampleValue}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </ScrollArea>
  )
}

// ============================================
// Preview Step
// ============================================

function PreviewStep({
  rows,
  summary,
}: {
  rows: import('@/lib/csv/fuel-csv-parser').ParsedFuelRow[]
  summary: import('@/lib/csv/fuel-csv-parser').CsvParseResult['summary'] | null
}) {
  if (!summary) return null

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex gap-3 flex-wrap">
        <SummaryBadge
          icon={<CheckCircle2 className="h-3.5 w-3.5" />}
          label="Ready"
          count={summary.ready}
          variant="default"
        />
        <SummaryBadge
          icon={<AlertTriangle className="h-3.5 w-3.5" />}
          label="Warnings"
          count={summary.warnings}
          variant="secondary"
        />
        <SummaryBadge
          icon={<XCircle className="h-3.5 w-3.5" />}
          label="Errors"
          count={summary.errors}
          variant="destructive"
        />
        <SummaryBadge
          icon={<Copy className="h-3.5 w-3.5" />}
          label="Duplicates"
          count={summary.duplicates}
          variant="outline"
        />
      </div>

      {/* Rows table */}
      <ScrollArea className="h-[350px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-background z-10">
            <tr className="border-b">
              <th className="text-left py-2 pr-2 font-medium w-10">#</th>
              <th className="text-left py-2 pr-2 font-medium">Date</th>
              <th className="text-left py-2 pr-2 font-medium">Tail #</th>
              <th className="text-left py-2 pr-2 font-medium">Client</th>
              <th className="text-left py-2 pr-2 font-medium">Fuel</th>
              <th className="text-right py-2 pr-2 font-medium">Gallons</th>
              <th className="text-right py-2 pr-2 font-medium">Price</th>
              <th className="text-right py-2 pr-2 font-medium">Total</th>
              <th className="text-left py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.rowIndex} className="border-b hover:bg-muted/50">
                <td className="py-1.5 pr-2 text-muted-foreground">{row.rowIndex}</td>
                <td className="py-1.5 pr-2 font-mono text-xs">{row.invoiceDate ?? row.rawDate}</td>
                <td className="py-1.5 pr-2 font-mono text-xs">{row.rawTailNumber}</td>
                <td className="py-1.5 pr-2 text-xs truncate max-w-28">
                  {row.clientName ?? row.rawCustomerName}
                </td>
                <td className="py-1.5 pr-2 text-xs">
                  {row.fuelType === 'jet_a' ? 'Jet-A' : row.fuelType === 'avgas' ? '100LL' : row.rawProduct}
                </td>
                <td className="py-1.5 pr-2 text-right font-mono text-xs">
                  {row.gallons?.toLocaleString() ?? row.rawGallons}
                </td>
                <td className="py-1.5 pr-2 text-right font-mono text-xs">
                  {row.unitPrice != null ? `$${row.unitPrice.toFixed(4)}` : row.rawUnitPrice}
                </td>
                <td className="py-1.5 pr-2 text-right font-mono text-xs">
                  {row.gallons != null && row.unitPrice != null
                    ? formatFuelTotal(row.gallons * row.unitPrice)
                    : row.rawTotalAmount}
                </td>
                <td className="py-1.5">
                  <StatusBadge status={row.status} errors={row.errors} warnings={row.warnings} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollArea>
    </div>
  )
}

// ============================================
// Importing Step
// ============================================

function ImportingStep({ progress }: { progress: { current: number; total: number } }) {
  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-12">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <div className="w-full max-w-xs space-y-2">
        <Progress value={pct} />
        <p className="text-center text-sm text-muted-foreground">
          Importing transactions...
        </p>
      </div>
    </div>
  )
}

// ============================================
// Complete Step
// ============================================

function CompleteStep({ result }: { result: { inserted: number; errors: number } | null }) {
  if (!result) return null

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-12">
      <CheckCircle2 className="h-12 w-12 text-green-500" />
      <div className="text-center space-y-1">
        <p className="text-lg font-semibold">
          {result.inserted} transaction{result.inserted !== 1 ? 's' : ''} imported
        </p>
        {result.errors > 0 && (
          <p className="text-sm text-destructive">
            {result.errors} row{result.errors !== 1 ? 's' : ''} failed
          </p>
        )}
      </div>
    </div>
  )
}

// ============================================
// Helpers
// ============================================

function SummaryBadge({
  icon,
  label,
  count,
  variant,
}: {
  icon: React.ReactNode
  label: string
  count: number
  variant: 'default' | 'secondary' | 'destructive' | 'outline'
}) {
  return (
    <Badge variant={variant} className="gap-1 text-xs py-1 px-2">
      {icon}
      {count} {label}
    </Badge>
  )
}

function StatusBadge({
  status,
  errors,
  warnings,
}: {
  status: import('@/lib/csv/fuel-csv-parser').RowStatus
  errors: string[]
  warnings: string[]
}) {
  const tooltip = [...errors, ...warnings].join('; ')

  switch (status) {
    case 'ready':
      return (
        <Badge variant="default" className="text-xs gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Ready
        </Badge>
      )
    case 'warning':
      return (
        <Badge variant="secondary" className="text-xs gap-1" title={tooltip}>
          <AlertTriangle className="h-3 w-3" />
          Warning
        </Badge>
      )
    case 'error':
      return (
        <Badge variant="destructive" className="text-xs gap-1" title={tooltip}>
          <XCircle className="h-3 w-3" />
          Error
        </Badge>
      )
    case 'duplicate':
      return (
        <Badge variant="outline" className="text-xs gap-1">
          <Copy className="h-3 w-3" />
          Duplicate
        </Badge>
      )
  }
}
